/**
 * SINGLE SOURCE OF TRUTH for the job form's collection handlers.
 *
 * JobBuilderPage (create) and JobEditPage (edit) are the same form over the
 * same shape of state. Every add / update / remove / reorder handler below
 * used to exist twice, once per page, and 31 of the 54 shared declarations had
 * already drifted apart. This module is the one copy.
 *
 * Two rules keep it that way:
 *
 * 1. Every write goes through the functional updater, setFormData(prev => ...).
 *    The edit page previously used setFormData({ ...formData }), which reads
 *    formData from the closure - two updates in the same tick silently drop
 *    one. Never reintroduce the spread form here.
 *
 * 2. Where create and edit genuinely need to differ, that is an argument or an
 *    option, never a second copy. See JobFormRows' preserveCustomRate for the
 *    pattern.
 *
 * Reads of formData outside an updater are only ever used to decide whether to
 * act and what to tell the user; the write itself is always computed from prev.
 */

import { useState, useCallback } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import { toast } from 'sonner';

import { DEFAULT_EXPENSE } from './JobFormRows';
import { DEFAULT_PHASE_RATES, defaultRateFor } from '../../lib/phaseRates';
import { kitItemPricing } from '../../lib/crewKit';
import { restorePackagedEquipment } from '../../lib/equipmentOrder';
import { crewClientRate } from '../../lib/jobFinancials';

/**
 * Build a new phase of the given type.
 *
 * Rate precedence is the user's saved default for that phase type, then the
 * built-in default for it, then 800. The edit page used to skip the middle
 * step and fall straight to 800, so adding a Travel phase while editing
 * produced 800 where creating produced 400.
 */
export function createPhase(type = 'production', rates = DEFAULT_PHASE_RATES) {
  return {
    id: crypto.randomUUID(),
    type,
    day_rate: defaultRateFor(type, rates),
    hourly_rate: null,
    base_hours: 10,
    estimated_days: 1,
    estimated_hours: 0,
    start_date: '',
    overtime_enabled: true,
    overtime_multiplier: 1.5,
    double_time_threshold: 15,
    double_time_multiplier: 2.0,
  };
}

/**
 * How long equipment is scheduled for by default: the production phase's
 * length, not one day. The edit page used to hardcode 1.
 */
export function getProductionPhaseDays(phases) {
  const productionPhase = (phases || []).find(p => p.type === 'production');
  return productionPhase?.estimated_days || 1;
}

/** dnd-kit id for an entry in equipment_display_order. */
export function dndIdForOrderItem(item) {
  if (item.type === 'package') return `pkg-${item.id}`;
  if (item.type === 'divider') return `div-${item.id}`;
  if (item.type === 'crew_equipment') return `crew-${item.id}`;
  return `eq-${item.id}`;
}

/** Reorder one array on formData in response to a dnd-kit drag. */
function reorderBy(setFormData, key, event, matches = (row, id) => row.id === id) {
  const { active, over } = event;
  if (!over || active.id === over.id) return;
  setFormData(prev => {
    const rows = prev[key] || [];
    const oldIndex = rows.findIndex(row => matches(row, active.id));
    const newIndex = rows.findIndex(row => matches(row, over.id));
    if (oldIndex === -1 || newIndex === -1) return prev;
    return { ...prev, [key]: arrayMove(rows, oldIndex, newIndex) };
  });
}

/** Replace one row of a collection, optionally deriving other fields from it. */
function updateRow(setFormData, key, index, field, value, derive) {
  setFormData(prev => {
    const rows = [...(prev[key] || [])];
    if (!rows[index]) return prev;
    const updated = { ...rows[index], [field]: value };
    if (derive) derive(updated, field, value);
    rows[index] = updated;
    return { ...prev, [key]: rows };
  });
}

/** Client rate = base rate plus markup. The markup is the margin. */
function applyMarkup(row, field, value) {
  if (field !== 'base_rate' && field !== 'markup_percent') return;
  const baseRate = field === 'base_rate' ? value : row.base_rate;
  const markup = field === 'markup_percent' ? value : row.markup_percent;
  row.client_rate = crewClientRate(baseRate, markup);
}

/**
 * @param {object}   ctx
 * @param {object}   ctx.formData      current form state
 * @param {Function} ctx.setFormData   its setter
 * @param {Array}    ctx.crewMembers   crew inventory
 * @param {Array}    ctx.equipment     equipment inventory
 * @param {Array}    ctx.equipmentKits saved equipment kits
 * @param {object}   ctx.defaultRates  the user's saved default phase rates
 */
export function useJobFormHandlers({
  formData,
  setFormData,
  crewMembers = [],
  equipment = [],
  equipmentKits = [],
  defaultRates = DEFAULT_PHASE_RATES,
}) {
  // Bumping this key remounts the kit Select so it clears after each pick.
  const [kitSelectKey, setKitSelectKey] = useState(0);
  const [activeEquipmentDragId, setActiveEquipmentDragId] = useState(null);

  const newPhase = useCallback(
    (type = 'production') => createPhase(type, defaultRates),
    [defaultRates]
  );

  const phaseDays = useCallback(
    () => getProductionPhaseDays(formData.phases),
    [formData.phases]
  );

  // --- Phases -------------------------------------------------------------

  const addPhase = () =>
    setFormData(prev => ({ ...prev, phases: [...prev.phases, newPhase('pre')] }));

  const updatePhase = (index, field, value) =>
    updateRow(setFormData, 'phases', index, field, value);

  const removePhase = (index) =>
    setFormData(prev => ({ ...prev, phases: prev.phases.filter((_, i) => i !== index) }));

  const handlePhaseDragEnd = (event) => reorderBy(setFormData, 'phases', event);

  // --- Crew labour --------------------------------------------------------

  const addCrewLabourFromSelect = (crewId) => {
    const crew = crewMembers.find(c => c.id === crewId);
    if (!crew) return;

    const clientRate = crewClientRate(crew.default_day_rate, crew.default_markup_percent);
    const row = {
      id: crypto.randomUUID(),
      crew_id: crew.id,
      crew_name: crew.name,
      crew_role: crew.role,
      start_date: '',
      days: 1,
      base_rate: crew.default_day_rate,
      day_type: crew.day_type,
      labour_rate_mode: 'day',
      markup_percent: crew.default_markup_percent,
      client_rate: clientRate,
      notes: '',
    };

    setFormData(prev => ({ ...prev, crew_labour: [...prev.crew_labour, row] }));
    toast.success('Crew member added');
  };

  const updateCrewLabour = (index, field, value) =>
    updateRow(setFormData, 'crew_labour', index, field, value, applyMarkup);

  const removeCrewLabour = (index) =>
    setFormData(prev => ({
      ...prev,
      crew_labour: prev.crew_labour.filter((_, i) => i !== index),
    }));

  const handleCrewDragEnd = (event) => reorderBy(setFormData, 'crew_labour', event);

  // --- Crew equipment -----------------------------------------------------

  const addCrewEquipmentFromSelect = (crewId) => {
    const crew = crewMembers.find(c => c.id === crewId);
    if (!crew) return;

    const defaults = crew.default_equipment || [];
    const items = defaults.length > 0
      ? defaults.map(item => {
          const { baseRate, markup } = kitItemPricing(item, crew);
          return {
            id: crypto.randomUUID(),
            crew_id: crew.id,
            crew_name: crew.name,
            description: item.description || '',
            quantity: 1,
            days: 1,
            base_rate: baseRate,
            markup_percent: markup,
            client_rate: crewClientRate(baseRate, markup),
          };
        })
      : [{
          id: crypto.randomUUID(),
          crew_id: crew.id,
          crew_name: crew.name,
          description: '',
          quantity: 1,
          days: 1,
          base_rate: 0,
          markup_percent: kitItemPricing(null, crew).markup,
          client_rate: 0,
        }];

    setFormData(prev => ({
      ...prev,
      crew_equipment: [...prev.crew_equipment, ...items],
      equipment_display_order: [
        ...prev.equipment_display_order,
        ...items.map(it => ({ type: 'crew_equipment', id: it.id })),
      ],
    }));
    toast.success(`Added ${items.length} equipment item${items.length > 1 ? 's' : ''} for ${crew.name}`);
  };

  // Self-billed crew equipment is charged to the client by the crew member
  // directly, so its client rate is zero. See jobFinancials for how that flows
  // through to profit.
  const updateCrewEquipment = (index, field, value) =>
    updateRow(setFormData, 'crew_equipment', index, field, value, (row) => {
      if (field === 'billed_direct') {
        if (value) {
          row.client_rate = 0;
        } else {
          const baseRate = parseFloat(row.base_rate) || 0;
          row.base_rate = baseRate;
          row.client_rate = baseRate + (baseRate * (row.markup_percent || 0) / 100);
        }
      } else if (field === 'base_rate' || field === 'markup_percent') {
        if (row.billed_direct) {
          row.client_rate = 0;
        } else {
          applyMarkup(row, field, value);
        }
      }
    });

  const removeCrewEquipment = (index) =>
    setFormData(prev => {
      const removed = prev.crew_equipment[index];
      return {
        ...prev,
        crew_equipment: prev.crew_equipment.filter((_, i) => i !== index),
        equipment_display_order: removed
          ? prev.equipment_display_order.filter(
              o => !(o.type === 'crew_equipment' && o.id === removed.id)
            )
          : prev.equipment_display_order,
      };
    });


  // --- Equipment ----------------------------------------------------------

  /** Build an equipment line item from an inventory record. */
  const equipmentRow = (item, quantity, days) => ({
    id: crypto.randomUUID(),
    inventory_id: item.id,
    name: item.name,
    description: item.description || '',
    quantity,
    scheduled_days: days,
    daily_rate: item.daily_rate,
    weekly_rate: item.weekly_rate || null,
    rate_mode: 'day',
    rate_override: null,
  });

  const addEquipment = (equipId) => {
    const item = equipment.find(e => e.id === equipId);
    if (!item) return;
    if (formData.equipment.some(e => e.inventory_id === equipId)) return;

    const row = equipmentRow(item, 1, phaseDays());
    setFormData(prev => ({
      ...prev,
      equipment: [...prev.equipment, row],
      equipment_display_order: [
        ...prev.equipment_display_order,
        { type: 'equipment', id: row.id },
      ],
    }));
  };

  const addKitEquipment = (kitId) => {
    if (!kitId) return;

    const kit = equipmentKits.find(k => k.id === kitId);
    if (!kit || !kit.equipment_items || kit.equipment_items.length === 0) {
      toast.error('This kit has no equipment items');
      return;
    }

    const days = phaseDays();
    const alreadyInJob = new Set(formData.equipment.map(e => e.inventory_id));

    // A kit item carries its own quantity - two lights means two lights. The
    // builder page used to add one of everything.
    const rows = kit.equipment_items
      .map(kitItem => ({
        kitItem,
        item: equipment.find(e => e.id === kitItem.equipment_id),
      }))
      .filter(({ item }) => item && !alreadyInJob.has(item.id))
      .map(({ kitItem, item }) => equipmentRow(item, kitItem.quantity || 1, days));

    if (rows.length > 0) {
      setFormData(prev => ({
        ...prev,
        equipment: [...prev.equipment, ...rows],
        equipment_display_order: [
          ...prev.equipment_display_order,
          ...rows.map(row => ({ type: 'equipment', id: row.id })),
        ],
      }));
      toast.success(`Added ${rows.length} item${rows.length > 1 ? 's' : ''} from "${kit.name}"`);
    } else {
      toast.info('All items from this kit are already added');
    }

    setKitSelectKey(prev => prev + 1);
  };

  const updateEquipment = (index, field, value) =>
    updateRow(setFormData, 'equipment', index, field, value, (row) => {
      // Switching between day and week rates clears any override, so the new
      // mode's base rate is what shows.
      if (field === 'rate_mode') row.rate_override = null;
    });

  const removeEquipment = (index) =>
    setFormData(prev => {
      const removed = prev.equipment[index];
      return {
        ...prev,
        equipment: prev.equipment.filter((_, i) => i !== index),
        equipment_display_order: removed
          ? prev.equipment_display_order.filter(
              o => !(o.type === 'equipment' && o.id === removed.id)
            )
          : prev.equipment_display_order,
      };
    });


  // --- Divider lines ------------------------------------------------------

  const addDivider = () =>
    setFormData(prev => ({
      ...prev,
      equipment_display_order: [
        ...prev.equipment_display_order,
        { type: 'divider', id: crypto.randomUUID(), label: '' },
      ],
    }));

  const updateDivider = (dividerId, label) =>
    setFormData(prev => ({
      ...prev,
      equipment_display_order: prev.equipment_display_order.map(o =>
        o.type === 'divider' && o.id === dividerId ? { ...o, label } : o
      ),
    }));

  const removeDivider = (dividerId) =>
    setFormData(prev => ({
      ...prev,
      equipment_display_order: prev.equipment_display_order.filter(
        o => !(o.type === 'divider' && o.id === dividerId)
      ),
    }));

  // --- The combined equipment / package / divider list --------------------

  // Note: equipment, crew equipment and packages are reordered together through
  // handleCombinedDragEnd, which moves entries in equipment_display_order. The
  // per-collection reorder handlers those lists used to declare were never
  // wired to anything in either page and are gone.

  const handleCombinedDragStart = (event) => setActiveEquipmentDragId(event.active.id);

  const handleCombinedDragEnd = (event) => {
    setActiveEquipmentDragId(null);
    reorderBy(setFormData, 'equipment_display_order', event,
      (row, id) => dndIdForOrderItem(row) === id);
  };

  // --- Packages -----------------------------------------------------------

  const removePackage = (packageId) => {
    setFormData(prev => {
      const pkg = prev.equipment_packages.find(p => p.id === packageId);
      const order = restorePackagedEquipment(
        prev.equipment_display_order, prev.equipment, pkg?.equipment_ids || [], packageId
      );
      return {
        ...prev,
        equipment_packages: prev.equipment_packages.filter(p => p.id !== packageId),
        equipment_display_order: order.filter(o => !(o.type === 'package' && o.id === packageId)),
      };
    });
    toast.success('Package removed');
  };


  const getPackageForEquipment = (inventoryId) =>
    formData.equipment_packages.find(pkg => pkg.equipment_ids.includes(inventoryId));

  // --- Expenses and hidden costs ------------------------------------------

  const addExpense = () =>
    setFormData(prev => ({
      ...prev,
      expenses: [...prev.expenses, { ...DEFAULT_EXPENSE, id: crypto.randomUUID() }],
    }));

  const updateExpense = (index, field, value) =>
    updateRow(setFormData, 'expenses', index, field, value);

  const removeExpense = (index) =>
    setFormData(prev => ({
      ...prev,
      expenses: prev.expenses.filter((_, i) => i !== index),
    }));

  const handleExpenseDragEnd = (event) => reorderBy(setFormData, 'expenses', event);

  const addHiddenCost = () =>
    setFormData(prev => ({
      ...prev,
      hidden_costs: [...prev.hidden_costs, { ...DEFAULT_EXPENSE, id: crypto.randomUUID() }],
    }));

  const updateHiddenCost = (index, field, value) =>
    updateRow(setFormData, 'hidden_costs', index, field, value);

  const removeHiddenCost = (index) =>
    setFormData(prev => ({
      ...prev,
      hidden_costs: prev.hidden_costs.filter((_, i) => i !== index),
    }));

  const handleHiddenCostDragEnd = (event) => reorderBy(setFormData, 'hidden_costs', event);

  return {
    // state the form list owns
    kitSelectKey,
    activeEquipmentDragId,
    // helpers
    createPhase: newPhase,
    getProductionPhaseDays: phaseDays,
    getPackageForEquipment,
    dndIdForOrderItem,
    // phases
    addPhase, updatePhase, removePhase, handlePhaseDragEnd,
    // crew labour
    addCrewLabourFromSelect, updateCrewLabour, removeCrewLabour, handleCrewDragEnd,
    // crew equipment
    addCrewEquipmentFromSelect, updateCrewEquipment, removeCrewEquipment,
    // equipment
    addEquipment, addKitEquipment, updateEquipment, removeEquipment,
    // dividers
    addDivider, updateDivider, removeDivider,
    // combined list
    handleCombinedDragStart, handleCombinedDragEnd,
    // packages
    removePackage,
    // expenses and hidden costs
    addExpense, updateExpense, removeExpense, handleExpenseDragEnd,
    addHiddenCost, updateHiddenCost, removeHiddenCost, handleHiddenCostDragEnd,
  };
}
